
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { useTranslation } from "react-i18next";
import { AIAnalysisResult } from "@/hooks/useAIAnalysis";

interface ExerciseAnalysisCardProps {
  exerciseAnalysis: AIAnalysisResult['exerciseAnalysis'];
}

const ExerciseAnalysisCard = ({ exerciseAnalysis }: ExerciseAnalysisCardProps) => {
  const { t } = useTranslation('ai-analysis');
  if (!exerciseAnalysis) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('exerciseAnalysis.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-3 gap-4">
          {Object.entries(exerciseAnalysis).map(([exercise, data]) => (
            <div key={exercise} className="p-4 border rounded-lg">
              <h4 className="font-semibold mb-3 capitalize">{exercise}</h4>
              <div className="space-y-3">
                {'accuracy' in data && (
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm">{t('exerciseAnalysis.accuracy')}</span>
                      <span className="text-sm font-medium">{data.accuracy}%</span>
                    </div>
                    <Progress value={data.accuracy} className="h-2" />
                  </div>
                )}
                
                {'consistency' in data && (
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm">{t('exerciseAnalysis.consistency')}</span>
                      <span className="text-sm font-medium">{data.consistency}%</span>
                    </div>
                    <Progress value={data.consistency} className="h-2" />
                  </div>
                )}

                {'distance' in data && (
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm">{t('exerciseAnalysis.distance')}</span>
                      <span className="text-sm font-medium">{data.distance}%</span>
                    </div>
                    <Progress value={data.distance} className="h-2" />
                  </div>
                )}

                <p className="text-sm text-gray-600">{data.performance}</p>
                
                {data.recommendations && (
                  <div>
                    <h5 className="text-xs font-medium text-gray-500 mb-1">{t('exerciseAnalysis.tips')}</h5>
                    <ul className="text-xs space-y-1">
                      {data.recommendations.map((rec, index) => (
                        <li key={index} className="text-gray-600">• {rec}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default ExerciseAnalysisCard;
